import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Input, 
  Button, 
  Space, 
  Alert, 
  Typography, 
  Spin, 
  Empty, 
  Table, 
  Tag, 
  Divider,
  Calendar,
  Tabs,
  Select,
  message,
  Tooltip,
  InputNumber,
  Badge,
  DatePicker,
  Modal,
  Form,
  Checkbox,
  Progress
} from 'antd';
import { 
  CalendarOutlined, 
  LinkOutlined, 
  SyncOutlined, 
  DownloadOutlined, 
  SearchOutlined,
  InfoCircleOutlined,
  CopyOutlined,
  ShareAltOutlined,
  HomeOutlined,
  ImportOutlined,
  StopOutlined
} from '@ant-design/icons';
import AdminPageLayout from './layout/AdminPageLayout';
import useAirbnbCalendarStore from '../../stores/airbnbCalendarStore';
import useBookingStore from '../../stores/bookingStore';
import usePlatformStore from '../../stores/platformStore';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

// Format date to DD-MMM (e.g., 04-Mar)
const formatDate = (date) => {
  if (!date) return '';
  return dayjs(date).format('DD-MMM');
};

const AirBNBCalendar = () => {
  const [calendarUrl, setCalendarUrl] = useState('https://www.airbnb.com/calendar/ical/1309440519495782794.ics?s=1985a7824f85c767b4d690b61d957f90');
  const [propertyId, setPropertyId] = useState(1);
  const [selectedPlatformId, setSelectedPlatformId] = useState(null);
  const [exportUrl, setExportUrl] = useState('');
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [form] = Form.useForm();
  
  const { 
    calendarData, 
    loading, 
    error, 
    fetchCalendarData,
    importBookingFromAirbnb,
    importedEvents
  } = useAirbnbCalendarStore();
  
  const { bookings, loading: bookingsLoading, error: bookingsError, fetchBookings } = useBookingStore();
  const { platforms, loading: platformsLoading, fetchPlatforms } = usePlatformStore();
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  
  // Generate the export URL based on the current domain and property ID
  useEffect(() => {
    const domain = window.location.origin;
    const url = `${domain}/api/platforms/export/calendar/${propertyId}/`;
    setExportUrl(url);
  }, [propertyId]);

  // Fetch bookings when component mounts or date range changes
  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      const filters = {
        check_in_after: dateRange[0].format('YYYY-MM-DD'),
        check_out_before: dateRange[1].format('YYYY-MM-DD')
      };
      fetchBookings(filters);
    } else {
      fetchBookings();
    }
  }, [fetchBookings, dateRange]);
  
  // Fetch platforms when component mounts
  useEffect(() => {
    fetchPlatforms();
  }, [fetchPlatforms]);
  
  // When platforms are loaded, try to find the Airbnb platform
  useEffect(() => {
    if (platforms && platforms.length > 0) {
      const airbnbPlatform = platforms.find(p => 
        p.name.toLowerCase().includes('airbnb')
      );
      if (airbnbPlatform) {
        setSelectedPlatformId(airbnbPlatform.id);
      } else if (platforms[0]) {
        setSelectedPlatformId(platforms[0].id);
      }
    }
  }, [platforms]);

  const handleFetchCalendar = async () => {
    if (!calendarUrl) return;
    await fetchCalendarData(calendarUrl);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        message.success('Calendar URL copied to clipboard');
      },
      (err) => {
        message.error('Failed to copy URL: ' + err);
      }
    );
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };
  
  const showImportModal = (event) => {
    if (!event) {
      message.warning('No event selected to import.');
      return;
    }
    
    // Check if the event is already imported
    if (importedEvents.includes(event.uid)) {
      message.info('This event has already been imported.');
      return;
    }
    
    setCurrentEvent(event);
    form.setFieldsValue({
      platformId: selectedPlatformId,
      propertyId: propertyId
    });
    
    setIsImportModalVisible(true);
  };
  
  const handleImport = async () => {
    try {
      const values = await form.validateFields();
      
      if (!currentEvent) {
        message.error('No event selected for import.');
        return;
      }
      
      // Show loading message
      message.loading('Importing booking from Airbnb...', 0);
      
      const result = await importBookingFromAirbnb(
        currentEvent, 
        values.platformId, 
        values.propertyId
      );
      
      // Dismiss loading message
      message.destroy();
      
      if (result.success) {
        setIsImportModalVisible(false);
        
        // Refresh bookings to show newly imported ones
        fetchBookings();
      }
    } catch (error) {
      console.error('Import validation failed:', error);
    }
  };

  const renderCalendarEvents = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16 }}>Fetching calendar data...</Paragraph>
        </div>
      );
    }

    if (error) {
      return (
        <Alert
          message="Error fetching calendar data"
          description={error}
          type="error"
          showIcon
        />
      );
    }

    if (!calendarData || !calendarData.events || calendarData.events.length === 0) {
      return (
        <Empty
          description="No calendar data available"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    // No longer filtering out "Not available" events
    const allEvents = calendarData.events;
    
    // Calculate how many events have been imported
    const importedCount = allEvents.filter(
      event => importedEvents.includes(event.uid)
    ).length;
    
    // Table display for calendar events
    const columns = [
      {
        title: 'Start Date',
        dataIndex: 'start',
        key: 'start',
        render: date => formatDate(date)
      },
      {
        title: 'End Date',
        dataIndex: 'end',
        key: 'end',
        render: date => formatDate(date)
      },
      {
        title: 'Summary',
        dataIndex: 'summary',
        key: 'summary',
        render: (summary) => {
          if (summary?.includes('Not available')) {
            return (
              <span>
                <StopOutlined style={{ color: '#ff4d4f', marginRight: '8px' }} />
                Blocked Period
              </span>
            );
          }
          return summary;
        }
      },
      {
        title: 'Status',
        key: 'status',
        render: (_, record) => {
          if (importedEvents.includes(record.uid)) {
            return <Tag color="green">Imported</Tag>;
          }
          
          if (record.summary?.includes('Not available')) {
            return <Tag color="red">Blocked</Tag>;
          }
          
          const today = new Date();
          const startDate = new Date(record.start);
          const endDate = new Date(record.end);
          
          if (endDate < today) {
            return <Tag color="default">Past</Tag>;
          }
          if (startDate <= today && today <= endDate) {
            return <Tag color="blue">Current</Tag>;
          }
          return <Tag color="orange">Upcoming</Tag>;
        }
      },
      {
        title: 'Action',
        key: 'action',
        render: (_, record) => (
          <Button 
            type="primary" 
            size="small"
            onClick={() => showImportModal(record)}
            disabled={importedEvents.includes(record.uid)}
          >
            Import
          </Button>
        ),
      }
    ];

    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <Space>
            {importedCount > 0 && (
              <Alert 
                message={`${importedCount} of ${allEvents.length} events imported`} 
                type="info" 
                showIcon 
              />
            )}
          </Space>
        </div>
        
        <Title level={4}>Calendar Events</Title>
        <Table 
          dataSource={calendarData.events} 
          columns={columns} 
          rowKey="uid"
          pagination={{ pageSize: 10 }}
          rowClassName={(record) => {
            if (importedEvents.includes(record.uid)) {
              return 'imported-row';
            }
            if (record.summary?.includes('Not available')) {
              return 'blocked-row';
            }
            return '';
          }}
        />
      </div>
    );
  };

  const renderMyBookings = () => {
    if (bookingsLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16 }}>Loading bookings...</Paragraph>
        </div>
      );
    }

    if (bookingsError) {
      return (
        <Alert
          message="Error loading bookings"
          description={bookingsError}
          type="error"
          showIcon
        />
      );
    }

    if (!bookings || bookings.length === 0) {
      return (
        <Empty
          description="No bookings found for the selected period"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    // Table display for bookings
    const columns = [
      {
        title: 'Customer',
        key: 'customer',
        render: (record) => {
          return record.customer_name || 'No customer';
        },
      },
      {
        title: 'Check-in',
        dataIndex: 'check_in',
        key: 'check_in',
        sorter: (a, b) => new Date(a.check_in) - new Date(b.check_in),
        render: date => formatDate(date)
      },
      {
        title: 'Check-out',
        dataIndex: 'check_out',
        key: 'check_out',
        render: date => formatDate(date)
      },
      {
        title: 'Platform',
        key: 'platform',
        render: (record) => record.platform_details ? record.platform_details.name : '-',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status) => {
          let color = 'default';
          if (status === 'confirmed') color = 'green';
          else if (status === 'pending') color = 'orange';
          else if (status === 'cancelled') color = 'red';
          
          return <Tag color={color}>{status.toUpperCase()}</Tag>;
        }
      },
      {
        title: 'Total Price',
        dataIndex: 'total_price',
        key: 'total_price',
        render: (price) => price ? `$${price}` : '-',
      },
    ];

    return (
      <div>
        <div style={{ marginBottom: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Title level={5}>Filter by Date Range</Title>
            <RangePicker 
              value={dateRange} 
              onChange={handleDateRangeChange} 
              style={{ width: '100%' }}
              allowClear={false}
            />
          </Space>
        </div>
        
        <Table 
          dataSource={bookings} 
          columns={columns} 
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
        
        <div style={{ marginTop: 24 }}>
          <Title level={4}>Booking Calendar View</Title>
          <Calendar 
            fullscreen={true}
            dateCellRender={(date) => {
              const matchingBookings = bookings.filter(booking => {
                const checkIn = dayjs(booking.check_in);
                const checkOut = dayjs(booking.check_out);
                return date.isSame(checkIn, 'day') || 
                       date.isSame(checkOut, 'day') || 
                       (date.isAfter(checkIn, 'day') && date.isBefore(checkOut, 'day'));
              });
              
              if (matchingBookings.length > 0) {
                return (
                  <div>
                    {matchingBookings.map((booking, index) => {
                      let type = 'default';
                      if (booking.status === 'confirmed') type = 'success';
                      else if (booking.status === 'pending') type = 'warning';
                      else if (booking.status === 'cancelled') type = 'error';
                      
                      const customerName = booking.customer_name 
                        ? booking.customer_name.substring(0, 12) 
                        : 'Guest';
                      
                      return (
                        <div key={index} style={{ marginBottom: 3 }}>
                          <Badge 
                            status={type} 
                            text={
                              <span style={{ fontSize: '11px' }}>
                                {customerName}
                              </span>
                            } 
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              }
              return null;
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <AdminPageLayout
      title="Airbnb Calendar"
      icon={<CalendarOutlined />}
      subtitle="Sync and view your Airbnb calendar"
      listItems={[
        'Connect to your Airbnb calendar using the iCal URL',
        'Import Airbnb bookings into your BnBManager system',
        'Share your BnBManager calendar with Airbnb',
        'View upcoming, current, and past bookings'
      ]}
    >
      <Tabs
        defaultActiveKey="import"
        items={[
          {
            key: 'my-bookings',
            label: 'My Bookings',
            icon: <HomeOutlined />,
            children: (
              <Card>
                <Title level={4}>My Bookings</Title>
                <Paragraph>
                  View and manage your bookings from all platforms. This calendar displays all confirmed, 
                  pending, and cancelled bookings from your BnBManager system.
                </Paragraph>
                
                {renderMyBookings()}
              </Card>
            ),
          },
          {
            key: 'import',
            label: 'Import from Airbnb',
            children: (
              <Card>
                <Title level={4}>Import Airbnb Calendar</Title>
                <Paragraph>
                  Enter your Airbnb iCal URL to sync and display your Airbnb booking calendar. 
                  You can find this URL in your Airbnb host dashboard under Calendar settings.
                  Once fetched, you can import individual Airbnb bookings into your BnBManager system.
                </Paragraph>
                
                <Space direction="vertical" style={{ width: '100%', marginBottom: 24 }}>
                  <Input
                    prefix={<LinkOutlined />}
                    value={calendarUrl}
                    onChange={(e) => setCalendarUrl(e.target.value)}
                    placeholder="Enter Airbnb iCal URL"
                    addonAfter={
                      <Button 
                        type="link" 
                        icon={<InfoCircleOutlined />}
                        onClick={() => window.open('https://www.airbnb.com/help/article/99/how-do-i-sync-my-airbnb-calendar-with-another-calendar', '_blank')}
                      >
                        Help
                      </Button>
                    }
                  />
                  
                  <Button
                    type="primary"
                    icon={<SyncOutlined />}
                    onClick={handleFetchCalendar}
                    loading={loading}
                  >
                    Fetch Calendar
                  </Button>
                </Space>
                
                <Divider />
                
                {renderCalendarEvents()}
                
                {calendarData && calendarData.events && calendarData.events.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <Title level={4}>Calendar View</Title>
                    <Calendar 
                      fullscreen={true}
                      dateCellRender={(date) => {
                        if (!calendarData || !calendarData.events) {
                          return null;
                        }
                        
                        const events = calendarData.events.filter(event => {
                          const eventStart = new Date(event.start);
                          const eventEnd = new Date(event.end);
                          return date >= eventStart && date <= eventEnd;
                        });
                        
                        if (events.length > 0) {
                          return (
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              {events.map((event, index) => (
                                <li key={index}>
                                  <Tag 
                                    color={importedEvents.includes(event.uid) ? "green" : "blue"}
                                  >
                                    {event.summary}
                                    {importedEvents.includes(event.uid) && " (Imported)"}
                                  </Tag>
                                </li>
                              ))}
                            </ul>
                          );
                        }
                        return null;
                      }}
                    />
                  </div>
                )}
                
                {/* Import Modal */}
                <Modal
                  title="Import Airbnb Booking"
                  open={isImportModalVisible}
                  onCancel={() => setIsImportModalVisible(false)}
                  onOk={handleImport}
                  okText="Import"
                  width={500}
                  confirmLoading={loading}
                >
                  <Form
                    form={form}
                    layout="vertical"
                  >
                    <Form.Item
                      name="platformId"
                      label="Select Platform"
                      rules={[{ required: true, message: 'Please select a platform' }]}
                      initialValue={selectedPlatformId}
                    >
                      <Select 
                        loading={platformsLoading}
                        disabled={platformsLoading}
                        placeholder="Select a platform"
                      >
                        {platforms && platforms.map(platform => (
                          <Select.Option key={platform.id} value={platform.id}>
                            {platform.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    
                    <Form.Item
                      name="propertyId"
                      label="Property ID"
                      rules={[{ required: true, message: 'Please enter a property ID' }]}
                      initialValue={propertyId}
                    >
                      <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    
                    <Divider />
                    
                    {currentEvent && (
                      <div style={{ 
                        padding: 16, 
                        background: currentEvent.summary?.includes('Not available') ? '#fff2f0' : '#f6ffed',
                        border: '1px solid #f0f0f0',
                        borderRadius: 4,
                        marginBottom: 16
                      }}>
                        <Title level={5}>
                          {currentEvent.summary?.includes('Not available') 
                            ? 'Blocked Period' 
                            : 'Reservation'}
                        </Title>
                        <Paragraph>
                          <strong>Period:</strong> {formatDate(currentEvent.start)} to {formatDate(currentEvent.end)}
                        </Paragraph>
                        {currentEvent.description && (
                          <Paragraph>
                            <strong>Description:</strong> {currentEvent.description}
                          </Paragraph>
                        )}
                      </div>
                    )}
                  </Form>
                </Modal>
              </Card>
            ),
          },
          {
            key: 'export',
            label: 'Export to Airbnb',
            children: (
              <Card>
                <Title level={4}>Export Calendar to Airbnb</Title>
                <Paragraph>
                  Airbnb allows you to import an external calendar to keep your listing availability up to date.
                  Use the URL below in your Airbnb host dashboard to sync your BnBManager bookings to Airbnb.
                </Paragraph>
                
                <div style={{ marginBottom: 24 }}>
                  <Title level={5}>Step 1: Select Property</Title>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <InputNumber
                        min={1}
                        value={propertyId}
                        onChange={(value) => setPropertyId(value)}
                        style={{ width: '100px' }}
                      />
                      <Text>Property ID</Text>
                    </div>
                    <Paragraph type="secondary">
                      This will export bookings for the selected property only.
                    </Paragraph>
                  </Space>
                </div>
                
                <div style={{ marginBottom: 24 }}>
                  <Title level={5}>Step 2: Copy Calendar URL</Title>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Input
                      readOnly
                      value={exportUrl}
                      addonAfter={
                        <Tooltip title="Copy URL">
                          <Button 
                            type="text" 
                            icon={<CopyOutlined />} 
                            onClick={() => copyToClipboard(exportUrl)}
                          />
                        </Tooltip>
                      }
                    />
                    <Paragraph type="secondary">
                      This URL provides access to your calendar in iCal format.
                    </Paragraph>
                  </Space>
                </div>
                
                <div style={{ marginBottom: 24 }}>
                  <Title level={5}>Step 3: Add to Airbnb</Title>
                  <ol style={{ paddingLeft: '16px' }}>
                    <li>Log in to your Airbnb host account</li>
                    <li>Go to "Calendar" in your hosting dashboard</li>
                    <li>Click "Availability settings" at the bottom of the page</li>
                    <li>Scroll to "Sync calendars" and click "Import Calendar"</li>
                    <li>Paste the URL above and name it "BnBManager"</li>
                    <li>Click "Import"</li>
                  </ol>
                  <Paragraph>
                    <a 
                      href="https://www.airbnb.com/help/article/99/how-do-i-sync-my-airbnb-calendar-with-another-calendar" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      View Airbnb's guide to calendar sync
                    </a>
                  </Paragraph>
                </div>
              </Card>
            ),
          },
        ]}
      />
      
      <style jsx="true">{`
        .imported-row {
          background-color: #f6ffed;
        }
        .blocked-row {
          background-color: #fff2f0;
        }
      `}</style>
    </AdminPageLayout>
  );
};

export default AirBNBCalendar;